import React, { useState } from 'react';
import ReportShell from './ReportShell';
import useReport from './useReport';
import RankedBars from './charts/RankedBars';

const pct = (n) => `${Math.round((n || 0) * 100)}%`;

export default function FeatureScorecardReport() {
  const [population, setPopulation] = useState('organic');
  const { data, loading, error } = useReport('feature-scorecard', population, '&days=90');

  return (
    <ReportShell
      title="Feature Scorecard"
      headline={data?.headline}
      population={population}
      onPopulationChange={setPopulation}
      populationSize={data?.population_size ?? 0}
      loading={loading}
      error={error}
    >
      {data?.days && (
        <p className="report-note">Measured over the last {data.days} days.</p>
      )}
      <RankedBars
        max={1}
        rows={(data?.features || []).map((f) => ({
          key: f.key,
          label: f.label,
          value: f.tried_rate,
          caption: `${Math.round(f.tried_rate * 100)}% (${f.tried})`,
        }))}
      />
      <div className="report-table-wrap">
        <table className="report-table">
          <thead>
            <tr>
              <th>Feature</th><th>Tried</th><th>% of population</th>
              <th>Completed</th><th>% of those who tried</th><th>Came back</th>
            </tr>
          </thead>
          <tbody>
            {/* Sorted by tried_rate descending to match the RankedBars chart
                above -- RankedBars always sorts its own rows that way (see
                its docstring), so a table left in the backend's catalog
                order would silently drift out of alignment with the chart
                sitting right above it. */}
            {[...(data?.features || [])].sort((a, b) => b.tried_rate - a.tried_rate).map((f) => (
              <tr key={f.key}>
                <td>{f.label}{f.thin && <span className="report-thin-flag" title="Small sample"> ·</span>}</td>
                <td>{f.tried}</td>
                <td>{pct(f.tried_rate)}</td>
                <td>{f.completed}</td>
                <td>
                  {f.has_distinct_completion === false ? (
                    <span
                      className="report-scorecard-dash"
                      title="No distinct completion event -- trying and completing this feature fire the same tracked screen, so this rate is always 100% by construction."
                    >
                      —
                    </span>
                  ) : pct(f.completed_rate_of_tried)}
                </td>
                <td>{f.repeated}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {(data?.features || []).some((f) => f.has_distinct_completion === false) && (
        <p className="report-note">
          <strong>“—” in % of those who tried:</strong> that feature has no
          distinct completion event -- trying it and finishing it fire the
          same tracked screen, so the rate would always read 100% and isn’t
          shown as a number that looks meaningful but isn’t.
        </p>
      )}

      {data?.paid_off_column && !data.paid_off_column.available && (
        <p className="report-note">
          <strong>No “paid off” column:</strong> {data.paid_off_column.reason}
        </p>
      )}
    </ReportShell>
  );
}
